import { useEffect, useState } from "react";
import SimpleButton from "../../../../shared/components/Button/Button";
import ButtonLoader from "../../../../shared/components/ButtonLoader";
import CustomModal from "../../../../shared/components/CustomModal";
import TextBox from "../../../../shared/components/TextBox";
import {
  ACTION_LABEL,
  BUTTON_LABEL,
  icons,
  LABELS,
  toastError,
  toastSuccess,
} from "../../../../shared/constants";
import { feedbackQuestionValidation } from "../../../../shared/validation/customer-feedback-config-validation";
import {
  FeedbackQuestion,
  useFeedbackQuestionsCreateMutation,
  useFeedbackQuestionsDeleteMutation,
  useFeedbackQuestionsListQuery,
  useFeedbackQuestionsUpdateMutation,
} from "../../../../Features/customerFeedback/customer-feedback-slice";

// Rating Questions admin panel (contracts #7–#10). Rendered inside the
// FeedbackConfig tab wrapper, so it has no Navbar of its own.
const RatingQuestions = () => {
  const [questions, setQuestions] = useState<FeedbackQuestion[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<FeedbackQuestion | null>(null);
  const [questionText, setQuestionText] = useState("");
  const [errors, setErrors]: any = useState({});
  const [deleting, setDeleting] = useState<FeedbackQuestion | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const { data, isSuccess, refetch } = useFeedbackQuestionsListQuery();

  const [createQuestion, { isLoading: createLoading }] =
    useFeedbackQuestionsCreateMutation();
  const [updateQuestion, { isLoading: updateLoading }] =
    useFeedbackQuestionsUpdateMutation();
  const [deleteQuestion] = useFeedbackQuestionsDeleteMutation();

  // Helper to sort questions with Active ones first then Inactive ones
  const sortAndNormalizeQuestions = (list: FeedbackQuestion[]) => {
    const active = list
      .filter((q) => q.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder);
    const inactive = list
      .filter((q) => !q.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder);

    const combined = [...active, ...inactive];
    return combined.map((q, idx) => ({
      ...q,
      displayOrder: idx + 1,
    }));
  };

  useEffect(() => {
    if (isSuccess && data) {
      const normalized = sortAndNormalizeQuestions(data.list);

      // Sync normalized displayOrder to DB if changed
      normalized.forEach((q) => {
        const orig = data.list.find((item) => item.questionId === q.questionId);
        if (orig && orig.displayOrder !== q.displayOrder) {
          updateQuestion({
            questionId: q.questionId,
            displayOrder: q.displayOrder,
          });
        }
      });

      setQuestions(normalized);
    }
  }, [data, isSuccess]);

  const activeCount = questions.filter((q) => q.isActive).length;
  const atMaxActive = activeCount >= 5;
  const atMaxTotal = questions.length >= 10;

  const openAdd = () => {
    if (questions.length >= 10) {
      toastError("Maximum 10 questions allowed");
      return;
    }
    setEditing(null);
    setQuestionText("");
    setErrors({});
    setShowForm(true);
  };

  const openEdit = (question: FeedbackQuestion) => {
    setEditing(question);
    setQuestionText(question.questionText);
    setErrors({});
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditing(null);
  };

  const handleSave = async () => {
    const { formValid, formErrors } = feedbackQuestionValidation({
      questionText,
    });
    if (!formValid) {
      setErrors(formErrors);
      return;
    }

    if (editing) {
      const res = await updateQuestion({
        questionId: editing.questionId,
        questionText: questionText.trim(),
      })
        .unwrap()
        .catch((err: any) => {
          toastError(err.data?.errors?.[0]?.msg);
        });
      if (res?.status) {
        toastSuccess("Question updated");
        refetch();
        handleClose();
      }
    } else {
      const res = await createQuestion({
        questionText: questionText.trim(),
        displayOrder: questions.length + 1,
        isActive: false,
      })
        .unwrap()
        .catch((err: any) => {
          toastError(err.data?.errors?.[0]?.msg);
        });
      if (res?.status) {
        toastSuccess(res.message || "");
        refetch();
        handleClose();
      }
    }
  };

  const handleToggleActive = async (question: FeedbackQuestion) => {
    const nextIsActive = !question.isActive;

    if (!nextIsActive && activeCount <= 1) {
      toastError(
        LABELS.MIN_ACTIVE_QUESTIONS_TEXT ||
          "At least one active rating question is required",
      );
      return;
    }

    if (nextIsActive && activeCount >= 5) {
      toastError(
        LABELS.MAX_ACTIVE_QUESTIONS_TEXT ||
          "Maximum 5 active rating questions allowed",
      );
      return;
    }

    let updatedList: FeedbackQuestion[];
    if (nextIsActive) {
      // Activating: place at the end of active questions
      const active = questions.filter(
        (q) => q.isActive && q.questionId !== question.questionId,
      );
      const inactive = questions.filter(
        (q) => !q.isActive && q.questionId !== question.questionId,
      );
      updatedList = [...active, { ...question, isActive: true }, ...inactive];
    } else {
      // Deactivating: place at the end of inactive questions
      const active = questions.filter(
        (q) => q.isActive && q.questionId !== question.questionId,
      );
      const inactive = questions.filter(
        (q) => !q.isActive && q.questionId !== question.questionId,
      );
      updatedList = [...active, ...inactive, { ...question, isActive: false }];
    }

    const reindexed = updatedList.map((q, idx) => ({
      ...q,
      displayOrder: idx + 1,
    }));
    setQuestions(reindexed);

    for (const q of reindexed) {
      const orig = questions.find((o) => o.questionId === q.questionId);
      if (
        !orig ||
        orig.isActive !== q.isActive ||
        orig.displayOrder !== q.displayOrder
      ) {
        await updateQuestion({
          questionId: q.questionId,
          isActive: q.isActive,
          displayOrder: q.displayOrder,
        })
          .unwrap()
          .catch((err: any) => {
            toastError(err.data?.errors?.[0]?.msg);
          });
      }
    }
    toastSuccess("Status updated");
    refetch();
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= questions.length) return;

    const list = [...questions];
    const temp = list[index];
    list[index] = list[target];
    list[target] = temp;

    // Re-index sequentially 1..N
    const reindexed = list.map((item, idx) => ({
      ...item,
      displayOrder: idx + 1,
    }));

    setQuestions(reindexed);

    // Save changes to DB for items whose displayOrder changed
    for (let i = 0; i < reindexed.length; i++) {
      if (reindexed[i].displayOrder !== questions[i]?.displayOrder) {
        await updateQuestion({
          questionId: reindexed[i].questionId,
          displayOrder: reindexed[i].displayOrder,
        }).unwrap();
      }
    }
    refetch();
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      return;
    }

    const list = [...questions];
    const itemToMove = list.splice(draggedIndex, 1)[0];
    list.splice(targetIndex, 0, itemToMove);

    // Re-index sequentially 1..N
    const reindexed = list.map((item, idx) => ({
      ...item,
      displayOrder: idx + 1,
    }));

    setQuestions(reindexed);
    setDraggedIndex(null);

    // Save changes to DB for items whose displayOrder changed
    for (let i = 0; i < reindexed.length; i++) {
      if (reindexed[i].displayOrder !== questions[i]?.displayOrder) {
        await updateQuestion({
          questionId: reindexed[i].questionId,
          displayOrder: reindexed[i].displayOrder,
        }).unwrap();
      }
    }
    refetch();
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    if (deleting.isActive && activeCount <= 1) {
      toastError(
        LABELS.MIN_ACTIVE_QUESTIONS_TEXT ||
          "At least one active rating question is required",
      );
      setDeleting(null);
      return;
    }
    const res = await deleteQuestion({ questionId: deleting.questionId })
      .unwrap()
      .catch((err: any) => {
        toastError(err.data?.errors?.[0]?.msg);
      });
    if (res?.status) {
      toastSuccess(res.message || "");
      refetch();
    }
    setDeleting(null);
  };

  return (
    <>
      <div className="alert alert-info d-flex align-items-center gap-2 mb-3">
        <i className={icons.ICON_KEY} />
        <span>{LABELS.CONFIG_APPLIES_TO_NEW_TEXT}</span>
      </div>

      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <span className="text-muted">
            {LABELS.ACTIVE_NAME_TEXT}: {activeCount} / 5
          </span>
          {atMaxActive && (
            <span className="ms-2 text-danger">
              {LABELS.MAX_ACTIVE_QUESTIONS_TEXT}
            </span>
          )}
        </div>
        <SimpleButton
          className="btn theme-btn theme-btn-sm btn-inverse w-auto"
          onClick={openAdd}
          disabled={atMaxTotal}
          text={LABELS.ADD_QUESTION_TEXT}
        />
      </div>

      <div className="table-responsive">
        <table className="table table-header-shadow mb-0">
          <thead>
            <tr>
              <th>{LABELS.QUESTION_TEXT}</th>
              <th>{LABELS.DISPLAY_ORDER_TEXT}</th>
              <th>{LABELS.ACTIVE_NAME_TEXT}</th>
              <th>{LABELS.ACTIONS_TEXT}</th>
            </tr>
          </thead>
          <tbody>
            {questions.map((question, index) => (
              <tr
                key={question.questionId}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={() => setDraggedIndex(null)}
                style={{
                  cursor: "grab",
                  opacity: draggedIndex === index ? 0.4 : 1,
                  backgroundColor:
                    draggedIndex === index ? "#f5efe6" : undefined,
                  transition: "background-color 0.2s, opacity 0.2s",
                }}
              >
                <td>{question.questionText}</td>
                <td>{question.displayOrder}</td>
                <td>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={question.isActive}
                      onChange={() => handleToggleActive(question)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: question.isActive
                          ? "#3E2215"
                          : undefined,
                        borderColor: question.isActive ? "#3E2215" : undefined,
                      }}
                      aria-label={`${question.questionText} active`}
                    />
                  </div>
                </td>
                <td>
                  <div className="d-flex align-items-center gap-1">
                    <SimpleButton
                      className="btn theme-btn theme-btn-sm btn-inverse w-auto"
                      title={LABELS.MOVE_UP_TEXT}
                      onClick={() => handleMove(index, -1)}
                      text="↑"
                    />
                    <SimpleButton
                      className="btn theme-btn theme-btn-sm btn-inverse w-auto"
                      title={LABELS.MOVE_DOWN_TEXT}
                      onClick={() => handleMove(index, 1)}
                      text="↓"
                    />
                    <SimpleButton
                      className="btn theme-btn theme-btn-sm btn-inverse w-auto"
                      onClick={() => openEdit(question)}
                      text={BUTTON_LABEL.EDIT}
                    />
                    <SimpleButton
                      className="btn theme-btn theme-btn-sm btn-inverse w-auto"
                      onClick={() => setDeleting(question)}
                      text={BUTTON_LABEL.DELETE}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {questions.length === 0 && (
              <tr>
                <td className="text-center" colSpan={4}>
                  <strong>{LABELS.No_DATA_FOUND_TEXT}</strong>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CustomModal
        show={showForm}
        close={handleClose}
        size={"md"}
        message={""}
        modalTitle={
          editing ? LABELS.EDIT_QUESTION_TEXT : LABELS.ADD_QUESTION_TEXT
        }
        modalButton={[
          {
            text: BUTTON_LABEL.CANCEL,
            action: handleClose,
            className: "btn theme-btn btn-inverse",
          },
          {
            text:
              createLoading || updateLoading ? (
                <ButtonLoader />
              ) : (
                BUTTON_LABEL.SAVE
              ),
            action: handleSave,
            className: "btn theme-btn",
          },
        ]}
        fullscreen={undefined}
        className={""}
        centerClass={""}
        modalTitleIcon={""}
      >
        <div className="form-group value-field">
          <TextBox
            name={ACTION_LABEL.QUESTION_TEXT}
            placeholder={LABELS.QUESTION_TEXT}
            id={LABELS.QUESTION_TEXT}
            label={LABELS.QUESTION_TEXT}
            onChange={(e: any) => {
              setQuestionText(e.target.value);
              setErrors({ ...errors, questionText: "" });
            }}
            value={questionText}
            errorMessage={errors.questionText || null}
          />
        </div>
      </CustomModal>

      <CustomModal
        show={!!deleting}
        close={() => setDeleting(null)}
        size={"sm"}
        message="Are you sure want to delete ?"
        modalTitle={"Delete"}
        modalButton={[
          {
            text: BUTTON_LABEL.CANCEL,
            action: () => setDeleting(null),
            className: "btn theme-btn btn-inverse",
          },
          {
            text: BUTTON_LABEL.CONFIRM,
            action: confirmDelete,
            className: "btn theme-btn",
          },
        ]}
        fullscreen={undefined}
        className={""}
        centerClass={""}
        modalTitleIcon={""}
      />
    </>
  );
};

export default RatingQuestions;
